import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWifi,
  faWifiStrong,
  faCircleExclamation,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { B2Accordion } from "@hdb/biome-2";
import InfiniteScroll from "react-infinite-scroll-component";
import lightOff from "../../../dashboard/components/dashboard-floor-plan/canvas/images/off-initial.png";
import lightOn from "../../../dashboard/components/dashboard-floor-plan/canvas/images/on-online-filled.png";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import * as moment from "moment";

const ASSET_TYPE_ORDER = ["Luminarie", "Gateway", "Sensor"];
const assetTypeSortOrder = (type: string): number => {
  const index = ASSET_TYPE_ORDER.indexOf(type);
  return index === -1 ? ASSET_TYPE_ORDER.length : index;
};

interface AssetAccordionBodyProps {
  isLoading: boolean;
  mergedAssetsData: any[];
  pageSize: number;
  isLoadingSelectedAssets: boolean;
  onAssetSelect: (assetType: string) => void;
  onClickViewMore: (assetType: string, totalCount: number) => void;
  navigateToFault: (
    tcCode: string,
    siteCode: string,
    blockCode: string
  ) => void;
  selectedTC: { tcCode: string; tcName?: string };
  selectedSiteCode?: string;
  selectedBlock: { blockCode: string; blockName?: string };
  SortBy: string;
  SortDirection: string;
  selectedLevel?: string;
  blockAssetsMap?: Record<string, any[]>;
  selectedAssets?: string[];
  onToggleAsset?: (assetCode: string) => void;
  allLevels?: Array<{ label: string; value: string }>;
  onLevelSelect?: (levelCode: string) => void;
  onLoadMore?: (assetType?: string) => void;
  hasMore?: Record<string, boolean>;
  isLoadingMore?: Record<string, boolean>;
  levelAccordionReset?: number;
}

interface LevelGroup {
  levelCode: string;
  levelName: string;
  assetTypes: Record<string, any[]>;
}

const groupAssetsByLevelAndType = (assets: any[]): LevelGroup[] => {
  const levelMap: Record<string, Record<string, any[]>> = {};

  assets.forEach((asset: any) => {
    const levelCode = asset?.floorCode || "Unknown";
    const assetType = asset?.assetType || "Unknown";

    if (!levelMap[levelCode]) {
      levelMap[levelCode] = {};
    }
    if (!levelMap[levelCode][assetType]) {
      levelMap[levelCode][assetType] = [];
    }
    levelMap[levelCode][assetType].push(asset);
  });

  return Object.entries(levelMap).map(([levelCode, assetTypes]) => ({
    levelCode,
    levelName: levelCode,
    assetTypes,
  }));
};

const AssetAccordionBody: React.FC<AssetAccordionBodyProps> = ({
  isLoading,
  mergedAssetsData,
  pageSize,
  isLoadingSelectedAssets,
  onAssetSelect,
  onClickViewMore,
  navigateToFault,
  selectedTC,
  selectedSiteCode,
  selectedBlock,
  SortBy,
  SortDirection,
  selectedLevel,
  blockAssetsMap = {},
  selectedAssets = [],
  onToggleAsset,
  allLevels,
  onLevelSelect,
  onLoadMore,
  hasMore = {},
  isLoadingMore = {},
  levelAccordionReset = 0,
}) => {
  const allAssets = useMemo(() => {
    if (!mergedAssetsData) return [];
    return mergedAssetsData.flatMap((group: any) => group.assets || []);
  }, [mergedAssetsData]);

  const levelGroups = useMemo(() => {
    if (!allAssets.length) return [];
    return groupAssetsByLevelAndType(allAssets);
  }, [allAssets]);

  const levelsToRender = useMemo(() => {
    if (allLevels && allLevels.length > 0) {
      return allLevels.map((level) => ({
        levelCode: level.value,
        levelName: level.label,
      }));
    }
    return levelGroups;
  }, [allLevels, levelGroups]);

  const renderAssetTypeGroup = (
    blockCode: string,
    levelCode: string,
    assetType: string,
    assets: any[],
    groupKey: string
  ) => {
    const groupInfo = mergedAssetsData?.find((g: any) => g.label === assetType);
    const totalCount = groupInfo?.totalCount ?? assets.length;
    const groupChecked =
      assets.length > 0 && assets.every((a) => selectedAssets.includes(a.assetCode));
    const groupIndeterminate =
      !groupChecked &&
      assets.some((a) => selectedAssets.includes(a.assetCode));
    const typeHasMore = hasMore?.[assetType] ?? assets.length < totalCount;
    const typeIsLoadingMore = isLoadingMore?.[assetType] || false;

    const listContent = (
      <ul className="asset-list-ul">
        {assets.map((asset) => {
          const isSelected = selectedAssets.includes(asset.assetCode);
          return (
            <li
              key={asset.assetCode}
              className="asset-list-item fw-bold link ul-hover p-2"
              style={onToggleAsset ? { paddingLeft: "24px" } : undefined}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexGrow: 1 }}>
                  {onToggleAsset && (
                    <div style={{ width: "20px", height: "20px", flexShrink: 0, display: "flex", alignItems: "center" }}>
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleAsset(asset.assetCode);
                        }}
                        style={{ cursor: "pointer", width: "18px", height: "18px" }}
                      />
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 1fr", gap: "2px 24px", flexGrow: 1, alignItems: "start" }}>
                    <div className="fw-bold" style={{ color: "#1976d2", textAlign: "left", minWidth: 0 }}>
                      <button
                        type="button"
                        title="View Asset Details"
                        className="cursor-pointer border-0 bg-transparent p-0 fw-bold"
                        style={{ color: "#1976d2", display: "inline-block", padding: 0, textAlign: "left" }}
                        onClick={() => {
                          window.open(
                            `${window.location.origin}/asset/${btoa(
                              asset?.assetCode
                            )}`,
                            "_blank"
                          );
                          onAssetSelect(assetType);
                        }}
                      >
                        {asset?.assetCode}
                        <FontAwesomeIcon
                          icon={faUpRightFromSquare}
                          className="external-link"
                        />
                      </button>
                    </div>
                    <div className="fw-bold info-level-color">
                      Location:
                      <span className="ms-2">{asset?.address}</span>
                    </div>
                    <div className="fw-bold info-level-color">
                      Display Name:
                      <span className="ms-2">
                        {asset?.sensorDisplayName}
                      </span>
                    </div>
                    <div className="fw-bold info-level-color">
                      Last Online:
                      <span className="ms-2">{asset?.lastOnline ? moment(asset?.lastOnline).format("DD-MM-YYYY HH:mm:ss") : "-"}</span>
                    </div>
                  </div>
                </div>
                <div
                  className="d-flex flex-column flex-xl-row gap-2 align-items-center"
                >
                  <div>
                    {asset.status === "Online" ? (
                      <div style={{ color: "#0c8188" }}>
                        {asset?.assetType === "Gateway" ? (
                          <FontAwesomeIcon
                            icon={faWifi}
                            className="h5 ms-2"
                            title="Online"
                          />
                        ) : (
                          <img
                            className="h5 ms-2"
                            src={lightOn}
                            alt="Light On"
                            title="Light On"
                            width="25px"
                            height="25px"
                          />
                        )}
                      </div>
                    ) : asset.status === "Offline" ? (
                      <div style={{ color: "#B36500", display: "flex" }}>
                        <div style={{ position: "relative" }}>
                          {asset.assetType === "Gateway" ? (
                            <>
                              <FontAwesomeIcon
                                icon={faWifiStrong}
                                className="h5 ms-2"
                                title="Offline"
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "0px",
                                  left: "18.3px",
                                  fontSize: "16px",
                                }}
                              >
                                !
                              </div>
                            </>
                          ) : (
                            <img
                              className="h5 ms-2"
                              src={lightOff}
                              alt="Light Off"
                              title="Light Off"
                              width="25px"
                              height="25px"
                            />
                          )}
                        </div>
                      </div>
                    ) : asset.status === "Initial" ? (
                      <div style={{ color: "rgb(143 143 143)" }}>
                        <FontAwesomeIcon
                          icon={faWifi}
                          className="h5 ms-2"
                          title="Initial"
                        />
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    title="View Faults"
                    className="cursor-pointer border-0 bg-transparent p-0 fw-bold"
                    style={{ display: "flex" }}
                    onClick={() =>
                      navigateToFault(
                        selectedTC.tcCode,
                        selectedSiteCode || "",
                        selectedBlock.blockCode
                      )
                    }
                  >
                    <div
                      className="d-flex align-items-baseline"
                      style={{ color: "red" }}
                    >
                      <FontAwesomeIcon
                        icon={faCircleExclamation}
                        className="h5 ms-2"
                      />
                      <small style={{ color: "red" }} className="fs-6">
                        {asset.totalFaults || 0}
                      </small>
                    </div>
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );

    return (
      <div key={groupKey} className="mt-2">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={groupChecked}
            ref={(el) => {
              if (el) el.indeterminate = groupIndeterminate;
            }}
            readOnly
            id={`select-all-${groupKey}`}
            onClick={(e) => {
              e.preventDefault();
            }}
          />
          <label
            className="form-check-label fw-bold"
            htmlFor={`select-all-${groupKey}`}
          >
            {assetType} ({totalCount})
          </label>
        </div>
        {totalCount > pageSize ? (
          <InfiniteScroll
            dataLength={assets.length}
            next={() => onLoadMore?.(assetType)}
            hasMore={typeHasMore}
            height={680}
            className="asset-infinite-scroll"
            loader={
              typeIsLoadingMore ? (
                <div className="text-center p-2">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : null
            }
            endMessage={
              <p style={{ textAlign: "center", paddingTop: "10px" }}>
                <b>No More Data</b>
              </p>
            }
          >
            {listContent}
          </InfiniteScroll>
        ) : (
          listContent
        )}
      </div>
    );
  };

  return (
    <SkeletonComponent
      wrapperHeight="200px"
      skeletonHeight="55px"
      isLoading={isLoading}
      count={3}
    >
      {levelsToRender.length > 0 ? (
        <B2Accordion
          key={`${selectedBlock?.blockCode}-${levelAccordionReset}`}
          alwaysOpen={false}
          activeKey={selectedLevel || null}
          onSelect={(eventKey: string) => {
            if (onLevelSelect && eventKey) {
              onLevelSelect(eventKey);
            }
          }}
        >
          {levelsToRender.map((levelGroup) => {
            const matchedGroup = levelGroups.find(
              (lg) => lg.levelCode === levelGroup.levelCode
            );
            const assetTypes = matchedGroup?.assetTypes || {};
            const groupKey = `${selectedBlock.blockCode}-${levelGroup.levelCode}`;

            return (
              <B2Accordion.Item
                eventKey={levelGroup.levelCode}
                key={levelGroup.levelCode}
              >
                <B2Accordion.Header>
                  {levelGroup.levelName}
                </B2Accordion.Header>
                <B2Accordion.Body>
                  {Object.keys(assetTypes).length > 0 ? (
                    Object.entries(assetTypes)
                      .sort(([a], [b]) => assetTypeSortOrder(a) - assetTypeSortOrder(b))
                      .map(([assetType, assets]) => {
                        const assetGroupKey = `${groupKey}-${assetType}`;
                        return renderAssetTypeGroup(
                          selectedBlock.blockCode,
                          levelGroup.levelCode,
                          assetType,
                          assets,
                          assetGroupKey
                        );
                      })
                  ) : (
                    <p className="mt-3 mb-0">No Data Available</p>
                  )}
                </B2Accordion.Body>
              </B2Accordion.Item>
            );
          })}
        </B2Accordion>
      ) : (
        <p className="mt-3 mb-0">No Data Available</p>
      )}
    </SkeletonComponent>
  );
};

export default AssetAccordionBody;
